import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Search, Eye, CheckCircle2, AlertCircle,
  DollarSign, Clock, FileText, XCircle, Send, Loader2, X, Printer, Building2, Download, CreditCard,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const handleStripePayment = async (inv, token) => {
  try {
    const origin = window.location.origin;
    const res = await axios.post(`${API}/billing/invoice-checkout`, {
      invoice_id: inv.id,
      origin_url: origin,
    }, { headers: { Authorization: `Bearer ${token}` } });
    if (res.data.checkout_url) window.location.href = res.data.checkout_url;
  } catch (err) {
    toast.error(err?.response?.data?.detail || 'Payment session could not be created');
  }
};

const downloadPDF = (inv) => {
  const doc = new jsPDF();
  const W = 210; // A4 width mm
  const primary    = [30, 64, 175];   // blue-800
  const accent     = [59, 130, 246];  // blue-500
  const lightGray  = [248, 250, 252]; // slate-50
  const midGray    = [100, 116, 139]; // slate-500
  const dark       = [15, 23, 42];    // slate-900
  const white      = [255, 255, 255];

  /* ── TOP HEADER BAND ── */
  doc.setFillColor(...primary);
  doc.rect(0, 0, W, 38, 'F');

  // Accent stripe
  doc.setFillColor(...accent);
  doc.rect(0, 35, W, 3, 'F');

  // INVOICE wordmark
  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text('INVOICE', 14, 24);

  // Invoice number (right)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(inv.invoice_number || '—', W - 14, 16, { align: 'right' });
  doc.setFontSize(8);
  doc.text(`Issued: ${fmtDate(inv.issued_date || inv.created_at)}`, W - 14, 23, { align: 'right' });
  doc.text(`Due:    ${fmtDate(inv.due_date)}`, W - 14, 29, { align: 'right' });

  /* ── STATUS BADGE ── */
  const sColors = {
    draft:     [100, 116, 139],
    sent:      [37, 99, 235],
    paid:      [5, 150, 105],
    overdue:   [220, 38, 38],
    cancelled: [107, 114, 128],
  };
  const sc = sColors[inv.status] || sColors.draft;
  doc.setFillColor(...sc);
  doc.roundedRect(14, 42, 36, 8, 2, 2, 'F');
  doc.setTextColor(...white);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text((inv.status || 'DRAFT').toUpperCase(), 32, 47.5, { align: 'center' });

  /* ── FROM / TO / PROJECT (3-column info block) ── */
  const infoY = 56;
  doc.setTextColor(...midGray);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('FROM', 14, infoY);
  doc.text('BILL TO', 80, infoY);
  doc.text('PROJECT', 148, infoY);

  // Separator line
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(14, infoY + 2, W - 14, infoY + 2);

  doc.setTextColor(...dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(inv.contractor_name || 'Contractor', 14, infoY + 9);
  doc.text(inv.exhibitor_name || 'Exhibitor', 80, infoY + 9);
  doc.text(inv.project_title || '—', 148, infoY + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...midGray);
  if (inv.contractor_email) doc.text(inv.contractor_email, 14, infoY + 15);
  if (inv.exhibitor_email)  doc.text(inv.exhibitor_email, 80, infoY + 15);
  doc.text(`Currency: ${inv.currency || 'EUR'}`, 148, infoY + 15);

  /* ── LINE ITEMS TABLE ── */
  autoTable(doc, {
    startY: infoY + 26,
    margin: { left: 14, right: 14 },
    head: [['#', 'Description', 'Qty', 'Unit Price', 'Amount']],
    body: (inv.items || []).map((item, idx) => [
      idx + 1,
      item.description || '—',
      item.quantity || 1,
      `${inv.currency || 'EUR'} ${Number(item.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `${inv.currency || 'EUR'} ${Number(item.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    ]),
    styles: { fontSize: 8.5, cellPadding: 4, textColor: dark, lineColor: [226, 232, 240], lineWidth: 0.2 },
    headStyles: { fillColor: primary, textColor: white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: lightGray },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 82 },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 36, halign: 'right' },
      4: { cellWidth: 36, halign: 'right', fontStyle: 'bold' },
    },
    tableLineColor: [226, 232, 240],
    tableLineWidth: 0.2,
  });

  /* ── TOTALS BLOCK (right-aligned) ── */
  let ty = doc.lastAutoTable.finalY + 8;
  const totalsX = W - 14;
  const labelX  = W - 65;

  doc.setDrawColor(...accent);
  doc.setLineWidth(0.5);
  doc.line(labelX - 4, ty - 2, totalsX, ty - 2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...midGray);
  doc.text('Subtotal', labelX, ty + 5);
  doc.setTextColor(...dark);
  doc.text(`${inv.currency || 'EUR'} ${Number(inv.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, totalsX, ty + 5, { align: 'right' });

  if (inv.tax_rate > 0) {
    doc.setTextColor(...midGray);
    doc.text(`Tax (${(inv.tax_rate * 100).toFixed(0)}%)`, labelX, ty + 12);
    doc.setTextColor(...dark);
    doc.text(`${inv.currency || 'EUR'} ${Number(inv.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, totalsX, ty + 12, { align: 'right' });
    ty += 7;
  }

  // Total row with filled bg
  doc.setFillColor(...primary);
  doc.roundedRect(labelX - 6, ty + 15, totalsX - labelX + 20, 11, 2, 2, 'F');
  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('TOTAL', labelX, ty + 22);
  doc.text(`${inv.currency || 'EUR'} ${Number(inv.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, totalsX, ty + 22, { align: 'right' });

  /* ── NOTES SECTION ── */
  if (inv.notes) {
    const notesY = ty + 34;
    doc.setFillColor(...lightGray);
    doc.roundedRect(14, notesY, W - 28, 18, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...midGray);
    doc.text('NOTES', 19, notesY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...dark);
    const noteLines = doc.splitTextToSize(inv.notes, W - 38);
    doc.text(noteLines.slice(0, 2), 19, notesY + 12);
  }

  /* ── FOOTER ── */
  const pageH = doc.internal.pageSize.height;
  doc.setFillColor(...lightGray);
  doc.rect(0, pageH - 16, W, 16, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...midGray);
  doc.text('Generated by ExpoStand Platform · expostand.com', W / 2, pageH - 6, { align: 'center' });

  doc.save(`${inv.invoice_number || 'invoice'}.pdf`);
};

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     color: 'bg-slate-100 text-slate-600',    icon: FileText    },
  sent:      { label: 'Sent',      color: 'bg-blue-100 text-blue-700',      icon: Send        },
  paid:      { label: 'Paid',      color: 'bg-emerald-100 text-emerald-700',icon: CheckCircle2},
  overdue:   { label: 'Overdue',   color: 'bg-red-100 text-red-700',        icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500',      icon: XCircle     },
};

const fmt = (v, currency = 'EUR') => {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

export default function ExhibitorInvoices() {
  const { token } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/exhibitor/invoices?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidInvoice = params.get('paid_invoice');
    if (paidInvoice) {
      toast.success('Payment successful! Invoice marked as paid.');
      window.history.replaceState({}, '', window.location.pathname);
      load();
    }
    if (params.get('payment_cancelled')) {
      toast.info('Payment was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  const handleMarkPaid = async (invId) => {
    try {
      await axios.patch(`${API}/invoices/${invId}/status`, { status: 'paid' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice marked as paid');
      setInvoices(prev => prev.map(i => i.id === invId ? { ...i, status: 'paid' } : i));
      if (selectedInvoice?.id === invId) setSelectedInvoice(p => ({ ...p, status: 'paid' }));
    } catch { toast.error('Failed to update status'); }
  };

  const filtered = invoices.filter(i =>
    search === '' || i.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    i.project_title?.toLowerCase().includes(search.toLowerCase()) ||
    i.contractor_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0);
  const totalDue = invoices.filter(i => i.status === 'sent').reduce((s, i) => s + (i.total_amount || 0), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0);
  const countAll = invoices.length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
            <Receipt className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Invoices</h1>
            <p className="text-xs text-gray-500">{total} invoices received</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Paid', value: fmt(totalPaid, 'EUR'), icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Amount Due', value: fmt(totalDue, 'EUR'), icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Overdue', value: fmt(totalOverdue, 'EUR'), icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Total Invoices', value: countAll, icon: Receipt, color: 'text-slate-600', bg: 'bg-slate-50' },
          ].map(kpi => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 shadow-sm">
                <div className={`w-11 h-11 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                  <p className="text-xl font-black text-gray-900">{kpi.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices..." className="pl-9 h-9" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'sent', 'paid', 'overdue'].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold capitalize transition-all ${statusFilter === s ? 'bg-blue-600 text-white shadow' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-400'}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Invoice #', 'Contractor', 'Project', 'Amount', 'Due Date', 'Status', 'Action'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No invoices received yet</p>
                </td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedInvoice(inv)}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-gray-800">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-gray-700">{inv.contractor_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{inv.project_title || '—'}</td>
                  <td className="px-4 py-3 font-bold text-gray-900">{fmt(inv.total_amount, inv.currency)}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelectedInvoice(inv)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadPDF(inv)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="Download PDF">
                        <Download className="w-4 h-4" />
                      </button>
                      {(inv.status === 'sent' || inv.status === 'overdue') && (
                        <button
                          onClick={() => handleStripePayment(inv, token)}
                          className="px-2.5 py-1 text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1"
                          title="Pay with Stripe"
                        >
                          <CreditCard className="w-3 h-3" />Pay
                        </button>
                      )}
                      {inv.status === 'sent' && (
                        <button onClick={() => handleMarkPaid(inv.id)} className="px-2.5 py-1 text-xs font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors">
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <Receipt className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-black text-gray-900">{selectedInvoice.invoice_number}</h2>
                    <StatusBadge status={selectedInvoice.status} />
                  </div>
                  <p className="text-gray-500 text-sm">{selectedInvoice.project_title}</p>
                </div>
                <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">From Contractor</p>
                  <p className="font-semibold text-gray-800 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-gray-400"/>{selectedInvoice.contractor_name || 'Contractor'}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Due Date</p>
                  <p className="font-semibold text-gray-800">{fmtDate(selectedInvoice.due_date)}</p>
                </div>
              </div>

              <table className="w-full text-sm mb-4">
                <thead className="border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-2 text-xs font-bold text-gray-500 uppercase">Description</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Qty</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Unit Price</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(selectedInvoice.items || []).map((item, i) => (
                    <tr key={i}>
                      <td className="py-2.5 text-gray-800">{item.description}</td>
                      <td className="py-2.5 text-right text-gray-600">{item.quantity}</td>
                      <td className="py-2.5 text-right text-gray-600">{fmt(item.unit_price, selectedInvoice.currency)}</td>
                      <td className="py-2.5 text-right font-semibold text-gray-900">{fmt(item.total, selectedInvoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="ml-auto max-w-xs space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span><span>{fmt(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                </div>
                {selectedInvoice.tax_rate > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Tax ({(selectedInvoice.tax_rate * 100).toFixed(0)}%)</span>
                    <span>{fmt(selectedInvoice.tax_amount, selectedInvoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-black text-lg text-gray-900 border-t border-gray-200 pt-2">
                  <span>Total</span><span>{fmt(selectedInvoice.total_amount, selectedInvoice.currency)}</span>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  <strong>Notes:</strong> {selectedInvoice.notes}
                </div>
              )}

              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                {selectedInvoice.status === 'sent' && (
                  <Button onClick={() => handleMarkPaid(selectedInvoice.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />Confirm Payment
                  </Button>
                )}
                {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                  <Button
                    onClick={() => handleStripePayment(selectedInvoice, token)}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />Pay with Stripe
                  </Button>
                )}
                <Button variant="outline" onClick={() => downloadPDF(selectedInvoice)} className="flex items-center gap-2">
                  <Download className="w-4 h-4" />Download PDF
                </Button>
                <Button variant="outline" onClick={() => window.print()} className="ml-auto flex items-center gap-2">
                  <Printer className="w-4 h-4" />Print
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
